import { apiClient } from './apiClient';

export interface TemplateField {
  name: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'checkbox' | 'select' | 'auto';
  label: string;
  autoSource?: string | null;
  options?: string[] | null;
  required: boolean;
}

export interface TemplateListItem {
  id: string;
  code: string;
  name: string;
  description?: string;
  categoryId?: string;
  categoryName?: string;
  departmentId?: string;
  departmentName?: string;
  fieldCount: number;
  useCount: number;
  isActive: boolean;
  updatedAt: string;
}

export interface TemplateDetail {
  id: string;
  code: string;
  name: string;
  description?: string;
  categoryId?: string;
  categoryName?: string;
  departmentId?: string;
  departmentName?: string;
  docxFilename: string;
  docxSize: number;
  fields: TemplateField[];
  /** JSON form schema cho WYSIWYG render. Có thể null cho mẫu chưa custom. */
  formSchemaJson?: string | null;
  isActive: boolean;
  useCount: number;
  createdAt: string;
  updatedAt: string;
  /** V1.0.27: Phiếu này có yêu cầu ký số hay không. Default true. */
  requiresSignature?: boolean;
}

export const templateService = {
  async list(): Promise<TemplateListItem[]> {
    const { data } = await apiClient.get<TemplateListItem[]>('/templates');
    return data;
  },

  async getById(id: string): Promise<TemplateDetail> {
    const { data } = await apiClient.get<TemplateDetail>(`/templates/${id}`);
    return data;
  },

  async create(payload: {
    code: string;
    name: string;
    description?: string;
    categoryId?: string;
    departmentId?: string;
    isActive: boolean;
    file: File;
  }): Promise<TemplateDetail> {
    const form = new FormData();
    form.append('code', payload.code);
    form.append('name', payload.name);
    if (payload.description) form.append('description', payload.description);
    if (payload.categoryId) form.append('categoryId', payload.categoryId);
    if (payload.departmentId) form.append('departmentId', payload.departmentId);
    form.append('isActive', String(payload.isActive));
    form.append('file', payload.file);
    const { data } = await apiClient.post<TemplateDetail>('/templates', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async update(id: string, payload: {
    name: string;
    description?: string;
    categoryId?: string;
    departmentId?: string;
    isActive: boolean;
    fieldOverrides?: TemplateField[];
    /** JSON string. null = không đổi. Empty string = xóa schema. */
    formSchemaJson?: string | null;
    /** V1.0.27: phiếu có yêu cầu ký hay không. */
    requiresSignature?: boolean;
  }): Promise<TemplateDetail> {
    const { data } = await apiClient.put<TemplateDetail>(`/templates/${id}`, payload);
    return data;
  },

  async replaceFile(id: string, file: File): Promise<TemplateDetail> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<TemplateDetail>(`/templates/${id}/file`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/templates/${id}`);
  },

  async duplicate(sourceId: string, newCode: string, newName: string): Promise<TemplateDetail> {
    const { data } = await apiClient.post<TemplateDetail>(`/templates/${sourceId}/duplicate`, {
      newCode, newName,
    });
    return data;
  },

  /**
   * Render template với data → trả về PDF blob.
   */
  async renderPdf(id: string, data: Record<string, string>): Promise<Blob> {
    const response = await apiClient.post(`/templates/${id}/render`, data, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  /**
   * URL download .docx gốc (cần JWT, dùng window.open ko ổn → dùng fetch+blob trong UI).
   */
  downloadDocxUrl(id: string): string {
    return `/api/templates/${id}/download`;
  },

  /**
   * Phase A: Resolve signers từ template form_schema.
   * Trả về list signer slot đã pre-fill theo cấu hình FIXED_USER / ROLE.
   */
  async resolveSigners(
    templateId: string,
    contextDepartmentId?: string,
  ): Promise<ResolvedSignerSlot[]> {
    const { data } = await apiClient.post<ResolvedSignerSlot[]>(
      `/templates/${templateId}/resolve-signers`,
      { contextDepartmentId: contextDepartmentId ?? null },
    );
    return data;
  },
};

/** Phase A: 1 signer slot đã được resolve. */
export interface ResolvedSignerSlot {
  label: string;
  slotIndex: number;
  mode: 'FIXED_USER' | 'ROLE' | 'NONE';
  order?: number;
  required: boolean;
  signer?: {
    id: string;
    fullName: string;
    avatarUrl?: string;
    position?: string;
  };
  departmentName?: string;
  resolveNote?: string;
}
